import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTenantSlug, buildPlatformUrl } from '../config/api';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * Auth + tenant-membership gate for logged-in routes.
 *
 * Beyond the basic "is logged in?" check, this also enforces that a user
 * landing on a tenant subdomain (e.g. jspt.localhost) is still an active
 * member of that institution. If they were removed (membership soft-
 * deleted), we bounce them off the subdomain to the bare platform host so
 * they can keep using their account from platform context.
 *
 * Why frontend, when backend already filters data?
 *   - Backend `accessible_books_predicate` returns empty results for non-
 *     members (data-safe), but the user still sees the chrome / nav of a
 *     tenant they no longer belong to. That's confusing and looks broken.
 *   - This is a UX guard layered on top of backend data filtering, not a
 *     trust boundary. Backend remains the source of truth.
 *
 * Platform admins are exempt: they keep cross-tenant moderation freedom
 * regardless of personal membership.
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  // Track redirect kickoff so we don't render `children` for a frame
  // before window.location.replace fires.
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (redirecting) return;
    const tenantSlug = getTenantSlug();
    if (!tenantSlug) return;
    if (user.is_admin) return;

    const hasActiveMembership = !!user.institution_memberships?.some(
      m => m.institution_slug === tenantSlug,
    );
    if (hasActiveMembership) return;

    // Membership came back from /auth/me filtered to is_active=True, so
    // "missing" here means either never-a-member or soft-deleted. Either
    // way, get them off this subdomain.
    const path = `${window.location.pathname}${window.location.search}`;
    const platformUrl = buildPlatformUrl(path);
    if (platformUrl && platformUrl !== window.location.href) {
      setRedirecting(true);
      window.location.replace(platformUrl);
    }
  }, [user, loading, redirecting]);

  if (loading || redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

export default PrivateRoute;
